import React, { useEffect, useState } from 'react';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Badge, 
    Button, 
    Dropdown, 
    Form, 
    InputGroup,
    Alert,
    Spinner,
    Pagination,
    Modal
} from 'react-bootstrap';
import { 
    FaBell, 
    FaTrash, 
    FaCheck, 
    FaCheckDouble, 
    FaSync, 
    FaFilter, 
    FaSearch,
    FaUser,
    FaPills,
    FaCalendarAlt,
    FaFileAlt,
    FaExclamationTriangle,
    FaInfoCircle,
    FaShieldAlt,
    FaClock,
    FaEye,
    FaEyeSlash
} from 'react-icons/fa';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useSelector } from 'react-redux';
import moment from 'moment';
import './NotificationComponent.css';

const NotificationComponent = () => {
    const user = useSelector((state) => state.user?.loggedInUser);
    const {
        notifications,
        unreadCount,
        isLoading,
        isConnected,
        initializeNotifications,
        loadNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        retryConnection
    } = useNotifications();

    const [filteredNotifications, setFilteredNotifications] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedNotificationDetails, setSelectedNotificationDetails] = useState(null);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const itemsPerPage = 10;

    useEffect(() => {
        // Don't call initializeNotifications here since it's already handled by NotificationBell
        // The context will be initialized when the user first loads the app
        console.log('NotificationComponent mounted, using existing notification context');
    }, [user?.id]);

  useEffect(() => {
        filterAndSortNotifications();
    }, [notifications, searchTerm, statusFilter, typeFilter, priorityFilter, sortBy, sortOrder]);

    const filterAndSortNotifications = () => {
        let filtered = [...notifications];

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(notification =>
                notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                notification.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                notification.notification_type?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(notification => notification.status === statusFilter);
        }

        // Apply type filter
        if (typeFilter !== 'all') {
            filtered = filtered.filter(notification => notification.notification_type === typeFilter);
        }

        // Apply priority filter
        if (priorityFilter !== 'all') {
            filtered = filtered.filter(notification => notification.priority === priorityFilter);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue = a[sortBy];
            let bValue = b[sortBy];

            if (sortBy === 'created_at' || sortBy === 'read_at') {
                aValue = new Date(aValue || 0);
                bValue = new Date(bValue || 0);
            }

            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
    } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        setFilteredNotifications(filtered);
        setCurrentPage(1);
    };

    const getNotificationIcon = (notificationType, priority) => {
        const iconClass = `notification-icon ${priority?.toLowerCase() || 'normal'}`;
        
        const iconMap = {
            'PATIENT_REGISTRATION': <FaUser className={iconClass} />,
            'PATIENT_ASSIGNMENT': <FaUser className={iconClass} />,
            'MEDICATION_REMINDER': <FaPills className={iconClass} />,
            'APPOINTMENT_REMINDER': <FaCalendarAlt className={iconClass} />,
            'APPOINTMENT_SCHEDULED': <FaCalendarAlt className={iconClass} />,
            'FORM_SUBMITTED': <FaFileAlt className={iconClass} />,
            'FORM_APPROVED': <FaFileAlt className={iconClass} />,
            'SAFETY_ALERT': <FaShieldAlt className={iconClass} />,
            'DRUG_INTERACTION_ALERT': <FaExclamationTriangle className={iconClass} />,
            'ALLERGY_ALERT': <FaExclamationTriangle className={iconClass} />,
            'CRITICAL_LAB_RESULT': <FaExclamationTriangle className={iconClass} />,
            'LAB_RESULT_READY': <FaInfoCircle className={iconClass} />
        };

        return iconMap[notificationType] || <FaBell className={iconClass} />;
    };

    const getPriorityBadge = (priority) => {
        const priorityMap = {
            'LOW': { variant: 'secondary', text: 'Low' },
            'NORMAL': { variant: 'info', text: 'Normal' },
            'HIGH': { variant: 'warning', text: 'High' },
            'CRITICAL': { variant: 'danger', text: 'Critical' },
            'URGENT': { variant: 'danger', text: 'Urgent' }
        };

        const config = priorityMap[priority] || priorityMap['NORMAL'];
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'pending': { variant: 'warning', text: 'Pending' },
            'sent': { variant: 'info', text: 'Sent' },
            'delivered': { variant: 'success', text: 'Delivered' },
            'read': { variant: 'secondary', text: 'Read' },
            'acknowledged': { variant: 'success', text: 'Acknowledged' },
            'failed': { variant: 'danger', text: 'Failed' }
        };

        const config = statusMap[status] || statusMap['pending'];
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const handleMarkAsRead = async (notificationId) => {
        await markAsRead(notificationId);
    };

    const handleMarkAllAsRead = async () => {
        if (user?.id) {
            await markAllAsRead(user.id);
        }
    };

    const handleDeleteNotification = async () => {
        if (selectedNotification) {
            await deleteNotification(selectedNotification.notification_id);
            setShowDeleteModal(false);
            setSelectedNotification(null);
        }
    };

    const handleShowDetails = (notification) => {
        setSelectedNotificationDetails(notification);
        setShowDetailsModal(true);
    };

    const handleRefresh = async () => {
        if (user?.id) {
            await loadNotifications(user.id);
        }
    };

    const paginatedNotifications = filteredNotifications.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setTypeFilter('all');
        setPriorityFilter('all');
    };

    const hasActiveFilters = searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || priorityFilter !== 'all';

  return (
        <Container fluid className="notifications-page">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
            <div>
                            <h2 className="mb-1">
                                <FaBell className="me-2 text-primary" />
                                Notifications
                            </h2>
                            <p className="text-muted mb-0">
                                {isConnected ? (
                                    <span className="text-success">
                                        <FaCheck className="me-1" />
                                        Connected to real-time updates
                                    </span>
                                ) : (
                                    <span className="text-warning">
                                        <FaExclamationTriangle className="me-1" />
                                        Connection lost
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={isLoading}
                            >
                                {isLoading ? <Spinner animation="border" size="sm" /> : <FaSync />}
                                Refresh
                            </Button>
                            {!isConnected && (
                                <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={retryConnection}
                                >
                                    Retry Connection
                                </Button>
                            )}
                            <Button
                                variant="success"
                                size="sm"
                                onClick={handleMarkAllAsRead}
                                disabled={unreadCount === 0}
                            >
                                <FaCheckDouble className="me-1" />
                                Mark All Read
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Filters */}
            <Card className="mb-4">
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search notifications..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={2}>
                            <Form.Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="sent">Sent</option>
                                <option value="delivered">Delivered</option>
                                <option value="read">Read</option>
                                <option value="acknowledged">Acknowledged</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Form.Select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <option value="all">All Types</option>
                                <option value="PATIENT_REGISTRATION">Patient Registration</option>
                                <option value="PATIENT_ASSIGNMENT">Patient Assignment</option>
                                <option value="MEDICATION_REMINDER">Medication Reminder</option>
                                <option value="APPOINTMENT_REMINDER">Appointment Reminder</option>
                                <option value="FORM_SUBMITTED">Form Submitted</option>
                                <option value="SAFETY_ALERT">Safety Alert</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Form.Select
                                value={priorityFilter}
                                onChange={(e) => setPriorityFilter(e.target.value)}
                            >
                                <option value="all">All Priorities</option>
                                <option value="LOW">Low</option>
                                <option value="NORMAL">Normal</option>
                                <option value="HIGH">High</option>
                                <option value="CRITICAL">Critical</option>
                            </Form.Select>
                        </Col>
                    </Row>
                    {hasActiveFilters && (
                        <Row className="mt-3">
                            <Col>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={clearFilters}
                                >
                                    <FaFilter className="me-1" />
                                    Clear Filters
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Statistics */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <h4 className="text-primary">{notifications.length}</h4>
                            <small className="text-muted">Total Notifications</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <h4 className="text-warning">{unreadCount}</h4>
                            <small className="text-muted">Unread</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <h4 className="text-success">
                                {notifications.filter(n => n.status === 'read' || n.status === 'acknowledged').length}
                            </h4>
                            <small className="text-muted">Read</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <h4 className="text-danger">
                                {notifications.filter(n => n.priority === 'CRITICAL' || n.priority === 'URGENT').length}
                            </h4>
                            <small className="text-muted">Critical</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Notifications List */}
            {isLoading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Loading notifications...</p>
                </div>
            ) : paginatedNotifications.length === 0 ? (
                <Card>
                    <Card.Body className="text-center py-5">
                        <FaBell className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                        <h5>No notifications found</h5>
                        <p className="text-muted">
                            {hasActiveFilters 
                                ? 'Try adjusting your filters or search terms.'
                                : 'You\'re all caught up! No new notifications.'
                            }
                        </p>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    {paginatedNotifications.map((notification, index) => (
                        <Card 
                            key={notification.notification_id || index} 
                            className={`mb-3 notification-card ${notification.status === 'read' ? 'read' : 'unread'}`}
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="d-flex align-items-start flex-grow-1">
                                        <div className="notification-icon-wrapper me-3">
                                            {getNotificationIcon(notification.notification_type, notification.priority)}
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="mb-1 notification-title">
                                                    {notification.title}
                                                </h6>
                                                <div className="d-flex gap-2">
                                                    {getPriorityBadge(notification.priority)}
                                                    {getStatusBadge(notification.status)}
                                                </div>
                                            </div>
                                            <p className="text-muted mb-2 notification-message">
                                                {notification.message}
                                            </p>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex gap-3 text-muted small">
                                                    <span>
                                                        <FaClock className="me-1" />
                                                        {moment(notification.created_at).fromNow()}
                                                    </span>
                                                    <span className="text-capitalize">
                                                        {notification.notification_type?.replace('_', ' ').toLowerCase()}
                                                    </span>
            </div>
                                                <div className="d-flex gap-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleShowDetails(notification)}
                                                    >
                                                        <FaEye />
                                                    </Button>
                                                    {notification.status !== 'read' && (
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            onClick={() => handleMarkAsRead(notification.notification_id)}
                                                        >
                                                            <FaCheck />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedNotification(notification);
                                                            setShowDeleteModal(true);
                                                        }}
                                                    >
              <FaTrash />
            </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <Pagination>
                                <Pagination.First 
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev 
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                />
                                
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                                    return (
                                        <Pagination.Item
                                            key={pageNum}
                                            active={pageNum === currentPage}
                                            onClick={() => setCurrentPage(pageNum)}
                                        >
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                
                                <Pagination.Next 
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last 
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </>
            )}

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Notification</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this notification? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteNotification}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Notification Details Modal */}
            <Modal 
                show={showDetailsModal} 
                onHide={() => setShowDetailsModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Notification Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedNotificationDetails && (
                        <div>
                            <div className="d-flex align-items-center mb-3">
                                <div className="notification-icon-wrapper me-3">
                                    {getNotificationIcon(selectedNotificationDetails.notification_type, selectedNotificationDetails.priority)}
                                </div>
                                <div>
                                    <h5>{selectedNotificationDetails.title}</h5>
                                    <div className="d-flex gap-2">
                                        {getPriorityBadge(selectedNotificationDetails.priority)}
                                        {getStatusBadge(selectedNotificationDetails.status)}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <strong>Message:</strong>
                                <p className="mt-2">{selectedNotificationDetails.message}</p>
                            </div>

                            <Row>
                                <Col md={6}>
                                    <strong>Type:</strong>
                                    <p className="text-capitalize">
                                        {selectedNotificationDetails.notification_type?.replace('_', ' ').toLowerCase()}
                                    </p>
                                </Col>
                                <Col md={6}>
                                    <strong>Created:</strong>
                                    <p>{moment(selectedNotificationDetails.created_at).format('DD/MM/YYYY h:mm:ss a')}</p>
                                </Col>
                            </Row>

                            {selectedNotificationDetails.context_data && Object.keys(selectedNotificationDetails.context_data).length > 0 && (
                                <div className="mb-3">
                                    <strong>Context Data:</strong>
                                    <pre className="mt-2 bg-light p-2 rounded">
                                        {JSON.stringify(selectedNotificationDetails.context_data, null, 2)}
                                    </pre>
                                </div>
                            )}

                            {selectedNotificationDetails.related_entity_type && (
                                <div>
                                    <strong>Related Entity:</strong>
                                    <p className="text-capitalize">
                                        {selectedNotificationDetails.related_entity_type} - {selectedNotificationDetails.related_entity_id}
                                    </p>
                                </div>
                            )}
    </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                        Close
                    </Button>
                    {selectedNotificationDetails?.status !== 'read' && (
                        <Button 
                            variant="success" 
                            onClick={() => {
                                handleMarkAsRead(selectedNotificationDetails.notification_id);
                                setShowDetailsModal(false);
                            }}
                        >
                            Mark as Read
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
  );
};

export default NotificationComponent;
